<template>
  <funding-assessments-base :state="state">
    <div
      v-if="!isSingleStream"
      class="border-b border-grey-300"
    >
      <nav class="navigation container py-2">
        <ul class="flex items-center justify-center">
          <li class="navigation-item mr-2 text-xs font-bold">
            Filter:
          </li>
          <li
            v-for="stream in $page.stream_counts"
            :key="stream.key"
            class="navigation-item"
          >
            <label
              class="no-underline cursor-pointer font-semibold text-sm block border-2 rounded-full py-1 px-4 transition-all transition-fast"
              :class="selectedStream.length == 1 && isActive(stream.key) ? 'cursor-not-allowed' : 'cursor-pointer'"
              :style="isActive(stream.key) ? {...getStreamColorsByKey(stream.key)} : {}"
            >
              <input
                v-model="selectedStream"
                name="streams"
                type="checkbox"
                :value="stream.key || 'unknown'"
                class="sr-only"
                :disabled="selectedStream.length == 1 && isActive(stream.key)"
              />
              {{ stream.name }} ({{ stream.count }})
            </label>
          </li>
        </ul>
      </nav>
    </div>
    <div class="p-12">
      <div v-if="isContinuationPhase" class="mb-12 max-w-lg">
        <h2>This is a continuation marking phase.</h2>
        <p>
          This phase of marking is a continuation of the previous marking phase.
          As such, making changes to assessors has been disabled.
        </p>
        <p v-if="requiresAllAssessorsToGrade">
          The phase also requires that all assessors assess all available
          applications.
        </p>
      </div>
      <div class="overflow-auto xl:overflow-visible bg-white shadow-lg">
        <table class="w-full leading-tight min-w-xl">
          <thead>
            <tr class="border-b-4 border-grey-300">
              <th class="text-left p-2 w-64 border-r border-grey-300">
                Application
              </th>
              <th class="w-10">
                <button
                  v-if="!isContinuationPhase"
                  class="bg-primary-500 hover:bg-secondary-500 transition-all transition-slow font-bold text-white w-full h-12"
                  :disabled="isContinuationPhase"
                  @click="showAddAssessor = true"
                >
                  &plus;
                  <span class="sr-only">Add Assessors</span>
                </button>
              </th>
              <th
                v-for="assessor in assessors"
                :key="assessor.id"
                class="p-2 border-l border-grey-300 w-64"
                :class="{
                  'stripes-grey':
                    assessor.pivot.summary_email_sent_at || isContinuationPhase
                }"
              >
                <div class="relative group">
                  {{ assessor.name }}
                  <button
                    v-if="!assessor.pivot.summary_email_sent_at"
                    class="transition-all transition-slow group-hover:opacity-75 opacity-0 text-xs uppercase font-bold text-negative-800 bg-negative-100 absolute pin-t pin-r w-full h-full"
                    @click="removeAssessorByID(assessor.id)"
                  >
                    Remove
                  </button>
                  <span
                    v-else-if="!isContinuationPhase"
                    class="text-xs italic font-normal block"
                  >
                    Assessments assigned
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-if="applications.length">
              <tr
                v-for="application in applications"
                v-show="selectedStream.includes(application.stream)"
                :key="application.id"
                class="border-b border-grey-300 hover:bg-primary-50"
              >
                <th class="text-left p-2 border-r border-grey-300">
                  <span class="flex items-center">
                    <span
                      v-if="application.stream && !isSingleStream"
                      class="border rounded px-1 py-px align-center text-2xs inline-block leading-none mr-2"
                      :style="{...getStreamColorsByKey(application.stream)}"
                    >
                      {{ getStreamNameByKey(application.stream) }}
                    </span>
                    {{ application.ref }}
                  </span>
                  <small class="block font-normal text-xs truncate">{{
                    application.company.name
                  }}</small>
                  <div
                    v-if="!allNotificationsSent && !isContinuationPhase"
                    class="mt-2"
                  >
                    <button
                      v-if="
                        applicationAssessors[application.id].length !==
                          assessors.length
                      "
                      class="text-xs text-left uppercase font-bold border px-2 py-1 text-grey-500 border-grey-300 hover:border-positive-700 hover:bg-positive-100 hover:text-positive-800 transition-all transition-normal"
                      @click="addAllAssessorsToApplication(application.id)"
                    >
                      Add all
                    </button>
                    <button
                      v-if="applicationAssessors[application.id].length > 0"
                      class="text-xs text-left uppercase font-bold border px-2 py-1 text-grey-500 border-grey-300 hover:border-negative-700 hover:bg-negative-100 hover:text-negative-800 transition-all transition-normal"
                      @click="removeAllAssessorsFromApplication(application.id)"
                    >
                      Remove all
                    </button>
                  </div>
                </th>
                <td class="p-2 text-center">
                  {{ applicationAssessors[application.id].length }}
                </td>
                <td
                  v-for="assessor in assessors"
                  :key="assessor.id"
                  class="group p-2 text-center border-l border-grey-300 relative"
                  :class="{
                    'stripes-grey':
                      assessor.pivot.summary_email_sent_at ||
                      isContinuationPhase
                  }"
                >
                  <label
                    class="flex items-center justify-center h-10"
                    :class="{
                      'cursor-pointer':
                        !assessor.pivot.summary_email_sent_at &&
                        !isContinuationPhase,
                      'cursor-not-allowed':
                        assessor.pivot.summary_email_sent_at ||
                        isContinuationPhase
                    }"
                  >
                    <input
                      v-model="applicationAssessors[application.id]"
                      type="checkbox"
                      :value="assessor.id"
                      class="hidden"
                      :disabled="
                        assessor.pivot.summary_email_sent_at ||
                          isContinuationPhase
                      "
                    />
                    <template
                      v-if="
                        applicationAssessors[application.id].includes(
                          assessor.id
                        )
                      "
                    >
                      <span
                        class="w-6 flex items-center justify-center h-6 font-bold bg-primary-500 text-white"
                        >&check;</span
                      >
                    </template>
                    <template v-else>
                      <span
                        class="w-6 flex items-center justify-center h-6 font-bold bg-negative-500 text-white"
                        >&times;</span
                      >
                    </template>
                    <span class="sr-only"
                      >Assign {{ assessor.name }} to {{ application.ref }}</span
                    >
                  </label>
                  <div
                    v-if="
                      assessor.pivot.summary_email_sent_at &&
                        !isContinuationPhase
                    "
                    class="opacity-0 group-hover:opacity-100 absolute pin-t pin-l w-full h-full flex transition-all transition-fast"
                  >
                    <button
                      v-if="
                        applicationAssessors[application.id].includes(
                          assessor.id
                        )
                      "
                      class="bg-negative-500 text-negative-100 m-1 flex-1 focus:outline-none focus:bg-negative-700 focus:text-negative-50 transition-all transition-fast"
                      @click="
                        forceRemoveAssessorFromApplication(
                          application,
                          assessor
                        )
                      "
                    >
                      Remove
                    </button>
                    <button
                      v-else
                      class="bg-positive-500 text-positive-100 m-1 flex-1 focus:outline-none focus:bg-positive-700 focus:text-positive-50 transition-all transition-fast"
                      @click="
                        forceAddAssessorToApplication(application, assessor)
                      "
                    >
                      Assign
                    </button>
                  </div>
                </td>
              </tr>
            </template>
            <template v-else>
              <div class="py-12 text-center px-6">
                <h2>No applications are assigned yet.</h2>
                <p>
                  Feel free to add assessors in preperation. When assessments
                  are available, you will be able to assign them to these
                  assessors.
                </p>
                <div class="mx-auto mt-4 w-48">
                  <app-button @click="showAddAssessor = true">
                    Add Assessor
                  </app-button>
                </div>
              </div>
            </template>
          </tbody>
          <tfoot v-if="applications.length && assessors.length">
            <tr class="border-t-2 border-grey-300">
              <td
                class="border-r border-grey-300 stripes-grey"
                colspan="2"
              ></td>
              <td
                v-for="assessor in assessors"
                :key="assessor.id"
                class="text-center p-2 border-r border-grey-300"
                :class="{
                  'stripes-grey':
                    assessor.pivot.summary_email_sent_at || isContinuationPhase
                }"
              >
                {{ calculateTotalForAssessor(assessor.id) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div
        v-if="applications.length && !isContinuationPhase"
        class="flex mt-12 items-baseline"
      >
        <div class="w-48 mr-6">
          <app-button
            :disabled="sendingNotifications || allNotificationsSent"
            @click="sendAssessorNotifications"
          >
            {{ sendingNotifications ? "Assigning..." : "Assign Assessors" }}
          </app-button>
        </div>
        <span v-if="allNotificationsSent" class="italic"
          >All assessors have been notified</span
        >
        <span v-else class="italic"
          >You first need to assign assessors for thier assesments to become
          available.</span
        >
      </div>
    </div>
    <portal to="modals">
      <app-modal v-if="showAddAssessor" @close="showAddAssessor = false">
        <div class="w-full">
          <app-card
            class="is-borderless w-screen max-w-sm mx-auto overflow-hidden p-4"
          >
            <div class="mb-6">
              <label for="searchAssessor" class="font-bold block mb-4">
                <icon
                  icon="search"
                  class="w-6 h-6 icons-primary mr-2 align-middle"
                />
                Search existing members
              </label>
              <input
                id="searchAssessors"
                ref="assessorSearch"
                v-model="searchValue"
                type="search"
                placeholder="Search for a member by name or email"
                class="py-4 px-3 bg-grey-200 focus:outline-none w-full rounded"
                autofocus
                autocomplete="off"
                @keyup="search"
                @search="searchResults = []"
              />
              <p
                v-if="searchValue.length > 3 && !searchResults.length"
                class="text-negative-600 text-xs italic mt-1"
              >
                <small>Unable to find this user</small>
              </p>
            </div>
            <ul v-if="searchResults.length" class="list-reset">
              <li
                v-for="(result, index) in searchResults"
                :key="index"
                class="flex justify-between py-3 px-2"
                :class="{
                  'border-b': index !== searchResults.length - 1
                }"
              >
                <div class="mr-2">
                  <p>{{ result.name }} <span class="text-grey-600 whitespace-no-wrap text-xs block">{{ result.email }}</span></p>
                  <p
                    v-if="result.company !== null && result.company.name"
                    class="text-grey-700"
                  >
                    <small>{{ result.company.name | sentencecase }}</small>
                  </p>
                </div>
                <button
                  v-if="alreadyExists(result.id)"
                  type="button"
                  class="bg-negative-100 uppercase text-xs px-3 py-1 font-bold text-negative-800 focus:outline-none hover:bg-negative-200 focus:bg-negative-200 transition-all transition-normal"
                  @click="removeAssessorByID(result.id)"
                >
                  Remove
                </button>
                <button
                  v-else
                  type="button"
                  class="bg-primary-100 uppercase text-xs px-3 py-1 font-bold text-primary-800 focus:outline-none hover:bg-primary-200 focus:bg-primary-200 transition-all transition-normal"
                  @click="addAssessor(result)"
                >
                  Add
                </button>
              </li>
            </ul>
            <form v-else @submit.prevent="inviteAssessor">
              <h2 class="text-base mb-2">
                <icon
                  icon="user-add"
                  class="w-6 h-6 icons-primary mr-2 align-middle"
                />
                {{ addUserFormTitle }}
              </h2>
              <auth-input
                v-model="invite.name"
                label="Name"
                input-type="text"
                :required="true"
                placeholder="e.g. Joanne Smith"
                autocomplete="off"
              />
              <auth-input
                v-model="invite.email"
                label="Email"
                input-type="email"
                :required="true"
                placeholder="e.g. joanne.smith@domain.com"
                autocomplete="off"
              />
              <p v-if="searchValue.length < 3" class="italic mb-4 text-xs">
                A user will be invited to sign up to access their assessments
                where they become available.
              </p>
              <div class="md:w-1/2 mt-6">
                <app-button type="submit" :disabled="duplicateInvite">
                  Invite
                </app-button>
              </div>
            </form>
          </app-card>
        </div>
      </app-modal>
    </portal>
  </funding-assessments-base>
</template>

<script>
import { competitionStreamsMixin } from "../mixins/competitionStreams";

export default {
  name: 'FundingAssessmentsIndex',
  mixins: [competitionStreamsMixin],
  data() {
    return {
      sendingNotifications: false,
      showAddAssessor: false,
      searchResults: [],
      searchValue: "",
      invite: {
        name: "",
        email: ""
      },
      assessors: this.state.assessors ?? [],
      applicationAssessors: this.state.application_assessors,
      selectedStream: [],
    }
  },
  computed: {
    isContinuationPhase() {
      return !!this.state.marking_phase.continuation_of_marking_phase_id
    },
    requiresAllAssessorsToGrade() {
      return !!this.state.marking_phase.requires_grading_from_other_assessors
    },
    applications() {
      return this.state.applications ?? []
    },
    addUserFormTitle() {
      return this.searchValue.length > 3
        ? "Would you like to invite this assessor?"
        : "Invite assessor";
    },
    duplicateInvite() {
      return this.assessors.find(
        assessor => assessor.email === this.invite.email.toLowerCase()
      );
    },
    assessorIDsThatHaveBeenNotified() {
      return this.assessors.filter((assessor) => assessor.pivot.summary_email_sent_at ).map(assessor => assessor.id)
    },
    allNotificationsSent() {
      return this.assessors.length === this.assessorIDsThatHaveBeenNotified.length
    }
  },
  created() {
    for (let assessment in this.applicationAssessors) {
      this.$watch(`applicationAssessors.${assessment}`, function(val) {

        axios({
          method: 'put',
          url: window.location.href,
          data: {
            application_id: assessment,
            assessors: [...val]
          }
        }).catch(() => {
          alert('We are unable to add the assessor to the application, please try again.')
        })
      })
    }
  },
  methods: {
    calculateTotalForAssessor(assessor_id) {
      let total = 0;

      for (let assessment in this.applicationAssessors) {
        if(this.applicationAssessors[assessment].includes(assessor_id)) {
          total++
        }
      }

      return total;
    },
    alreadyExists(id) {
      return this.assessors.find(assessor => assessor.id === id);
    },
    stateAssessor(id) {
      return this.state.assessors.find(currAssessor => currAssessor.id == id);
    },
    inviteAssessor() {
      axios
        .post(window.location.href, {
          ...this.invite,
          id: null,
          invite: true
        })
        .then(({data}) => {
          this.invite.name = "";
          this.invite.email = "";
          this.$refs.assessorSearch.focus();
          return this.assessors.push(data);
        })
        .catch(() => alert('Unable to add assessor, please try again.'))
    },
    addAssessor(assessor) {
      axios
        .post(window.location.href, {
          id: assessor.id,
          invite: false
        })
        .then(({data}) => {
          return this.assessors.push(data);
        })
        .catch(() => alert('Unable to add assessor, please try again.'))
    },
    addAllAssessorsToApplication(application_id) {
      const allAssessorIDs = this.assessors.map(assessor => assessor.id);
      const assessorsToAdd = allAssessorIDs.filter(assessor => this.assessorIDsThatHaveBeenNotified.indexOf(assessor) < 0)
      if(this.applicationAssessors.hasOwnProperty(application_id)) {
        this.applicationAssessors[application_id] = [...this.applicationAssessors[application_id], ...assessorsToAdd]
      }
    },
    removeAllAssessorsFromApplication(application_id) {
      if(!confirm('Are you sure you want to remove all assessors from this application?')) return
      if(this.applicationAssessors.hasOwnProperty(application_id)) {
        this.applicationAssessors[application_id] = this.applicationAssessors[application_id].filter(assessor => this.assessorIDsThatHaveBeenNotified.indexOf(assessor) > -1)
      }
    },
    removeAssessorByID(id) {
      if(this.assessorIDsThatHaveBeenNotified.includes(id)) {
        return alert('You can not remove an assessor that has already been notified.')
      }
      if(!confirm('Are you sure you want to remove this assessor? WARNING: This will remove all the assessors scoring for this phase!')) return;
      axios({
        method: 'delete',
        url: window.location.href,
        data: {
          id: id,
        }
      }).then(() => {
        return this.assessors.splice(
          this.assessors.findIndex(assessor => assessor.id === id),
          1
        );
      }).catch(() => alert('Unable to remove assessor, please try again.'))
    },
    search(event) {
      const value = event.target.value;
      if (value.length < 3) return (this.searchResults = []);
      axios
        .post(route("api.search.users"), {
          s: value
        })
        .then(({ data }) => {
          this.searchResults = data;
        });
    },
    forceRemoveAssessorFromApplication(application, assessor) {
      const confirmPromptResponse = prompt(`Are you sure you want to remove ${assessor.name} from ${application.ref}? As this is a permanent operation, please type the reference for this application (${application.ref}) below to confirm this action.`)
      if(!confirmPromptResponse) return
      if(confirmPromptResponse !== application.ref) return alert('The reference you entered was incorrect. Removing this assessor was cancelled.')
      const assessorIndex = this.applicationAssessors[application.id].indexOf(assessor.id)
      this.applicationAssessors[application.id].splice(assessorIndex, 1)
    },
    forceAddAssessorToApplication(application, assessor) {
      const confirmPromptResponse = prompt(`Are you sure you want to assign ${assessor.name} to ${application.ref}? Please type the reference for this application (${application.ref}) below to confirm this action.`)
      if(!confirmPromptResponse) return
      if(confirmPromptResponse !== application.ref) return alert('The reference you entered was incorrect. Adding this assessor was aborted.')
      this.applicationAssessors[application.id].push(assessor.id)
    },
    sendAssessorNotifications() {
      if(!confirm('Are you sure you want to send a notification to all assessors? WARNING: This will lock all the assignment panel and their pool of assessments.')) return
      this.sendingNotifications = true
      return axios({
        method: 'post',
        url: route("funding.assessments.assessors.notify", {
            funding: this.state.funding_pot.id,
            phase: this.state.marking_phase.id
          })
      }).then(({data}) => {
        this.assessors = data
      })
      .catch(() => alert("Unable to send notifications, please try again."))
      .finally(() => this.sendingNotifications = false);
    },
    isActive(stream) {
      return this.selectedStream.includes(stream);
    },
  },
  mounted() {
    this.selectedStream = this.$page.competition_streams?.map(s => s.key) ?? []
  }
}
</script>
